<script lang="ts">
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import type { AuthLog } from './log_defs'
    import { toLuxon } from 'hydria/utils/time'
    import Date from 'hydria/components/Date.svelte'
    import Icon from '@iconify/svelte'
    import { gettext as _ } from 'hydria/services/gettext'
    import { getContext, onDestroy, onMount } from 'svelte'
    import type { Writable } from 'svelte/store'

    let stoken = getContext('securityToken') as Writable<string>
    interface Props {
        updInc: Writable<number>
    }

    let { updInc }: Props = $props()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })
    let loading = $state(true)

    let entries: AuthLog[] = $state([])

    async function load(initial: boolean = false) {
        let r: SimpleFetchResult
        if (loading && !initial) {
            return
        }

        loading = true
        try {
            r = await simpleFetch('GET', '/json/security/1/log', undefined, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }

        if (!r || !r.data || !r.data.success) {
            let error = r && r.data && r.data.error ? r.data.error : 'Undefined error'
            console.error('Error loading auth log:', error)
            return
        }

        entries = []
        for (const e of r.data.entries) {
            e.stamp = toLuxon(e.stamp)

            entries.push(e)
        }

        loading = false
    }

    onMount(() => {
        load(true)
    })

    function totpText(t: string | null) {
        if (t == 'totp') return $_('(App)')
        if (t == 'sms') return $_('(SMS)')
        if (t == 'email') return $_('(Courriel)')
        return ''
    }

    unsubs.push(
        updInc.subscribe(() => {
            if (!loading) load()
        }),
    )
</script>

<style>
    .log-table {
        display: grid;
        grid-template-columns: 11em 9.5em 3fr 5em 2fr;
        grid-gap: 0.25rem 0.5rem;
    }
    /* .log-table > * {
        padding: 0 0.125rem;
    } */
    .h {
        font-weight: 500;
        width: 100%;
        border-bottom: 1px solid var(--p-grayish);
    }
    .log-table > * {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        word-break: keep-all;
        word-wrap: normal;
    }
    /* .stamp {
        padding-left: 0 !important;
    } */
    .ip {
        font-feature-settings:
            'ss01' 1,
            'tnum' 1;
        justify-self: center;
    }
    .success {
        justify-self: center;
    }
    /* .related {
        padding-right: 0 !important;
    } */
    .h.ip,
    .h.success {
        text-align: center;
    }
</style>

{#if loading}
    <LoadingPattern pattern={[100, 100, '-', 100, 100]} />
{:else if entries.length < 1}
    <p>{$_('Aucune entrée')}</p>
{:else}
    <div class="log-table">
        <div class="h stamp">{$_('Moment')}</div>
        <div class="h ip">{$_('Adresse IP')}</div>
        <div class="h action">{$_('Sujet')}</div>
        <div class="h success">{$_('Succès')}</div>
        <div class="h related">{$_('Type connexe')}</div>
        {#each entries as entry}
            <div class="stamp"><Date value={entry.stamp} format="h-table-s" /></div>
            <div class="ip">{entry.request_from_ip ? entry.request_from_ip : '–'}</div>
            <div class="action">
                {#if entry.action == 'signin'}
                    {$_('Connexion')}
                {:else if entry.action == 'signout'}
                    {$_('Déconnexion')}
                {:else if entry.action == 'set-password'}
                    {$_('Configuration du mot de passe')}
                {:else if entry.action == 'set-email'}
                    {$_('Configuration de l’adresse courriel du compte')}
                {:else if entry.action == 'lock'}
                    {$_('Compte verrouillé (trop de tentatives)')}
                {:else if entry.action == 'unlock'}
                    {$_('Compte déverrouillé')}
                {:else if entry.action == 'lost-password-email'}
                    {$_('Demande de courriel de récupération de mot de passe')}
                {:else if entry.action == 'welcome'}
                    {$_('Courriel de bienvenue')}
                {:else if entry.action == 'twofa-presignin'}
                    {$_('Mot de passe accepté, second facteur requis')} {totpText(entry.reference_type)}
                {:else if entry.action == 'add-otp'}
                    {$_('Ajout d’un mode connexion à deux facteurs')} {totpText(entry.e_type)}
                {:else if entry.action == 'remove-otp'}
                    {$_('Retrait un mode connexion à deux facteurs')} {totpText(entry.e_type)}
                {:else if entry.action == 'add-passkey'}
                    {$_('Ajout d’une clé d’accès')} {totpText(entry.e_type)}
                {:else if entry.action == 'remove-passkey'}
                    {$_('Retrait d’une clé d’accès')} {totpText(entry.e_type)}
                {/if}
            </div>
            <div class="success" class:text-danger={!entry.success}>
                <Icon inline icon={entry.success ? 'mdi:check-circle' : 'mdi:alert'} />
            </div>
            <div class="related">
                {#if entry.reference_type == 'password'}
                    <Icon inline icon="mdi:password" /> {$_('Mot de passe')}
                {:else if entry.reference_type == 'passkey'}
                    <Icon inline icon="mdi:shield-key" />
                    {$_('Clé d’accès')}
                    {#if entry.p_name}{$_(' (« {name} »)', { name: entry.p_name })}{/if}
                {:else if entry.reference_type == 'totp'}
                    <Icon inline icon="mdi:cellphone" /> {$_('Application')}
                {:else if entry.reference_type == 'sms'}
                    <Icon inline icon="mdi:message-text" />
                    {$_('Code par SMS')}
                    {#if entry.e_name}{$_(' (« {name} »)', { name: entry.e_name })}{/if}
                {:else if entry.reference_type == 'email'}
                    <Icon inline icon="mdi:email" />
                    {$_('Code par courriel')}
                    {#if entry.e_name}{$_(' (« {name} »)', { name: entry.e_name })}{/if}
                {:else if entry.reference_type == 'request-token'}
                    <Icon inline icon="mdi:email-lock" /> {$_('Récupération par courriel')}
                {:else if entry.reference_type == 'admin'}
                    <Icon inline icon="mdi:administrator" /> {$_('Administration')}
                {:else if entry.reference_type == 'recovery'}
                    <Icon inline icon="mdi:key" /> {$_('Code de récupération à usage unique')}
                {/if}
            </div>
        {/each}
    </div>
{/if}
